/**
 * useChangeFilter — client-side filter for the comparison change list.
 *
 * Filters changes by ChangeType, DecisionStatus and (P2-5) semantic tags
 * without touching the server. Designed to sit between the raw
 * `comparison.changes` array and the navigation / view hooks so that j/k
 * keyboard shortcuts operate on the filtered subset.
 */
import { useCallback, useMemo, useState } from 'react';

import type {
  ChangeResponse,
  ChangeType,
  DecisionStatus,
  SemanticTag,
} from '../../lib/veridoc-api';

const ALL_TYPES: ReadonlySet<ChangeType> = new Set([
  'addition',
  'deletion',
  'modification',
  'move',
  'format',
]);

const DEFAULT_TYPES: ReadonlySet<ChangeType> = new Set([
  'addition',
  'deletion',
  'modification',
  'move',
]);

const ALL_STATUSES: ReadonlySet<DecisionStatus> = new Set([
  'pending',
  'accepted',
  'rejected',
]);

const ALL_SEMANTIC_TAGS: readonly SemanticTag[] = ['amount', 'date', 'term'] as const;

export interface UseChangeFilterResult {
  typeFilter: Set<ChangeType>;
  statusFilter: Set<DecisionStatus>;
  semanticTagFilter: Set<SemanticTag>;
  searchTerm: string;
  toggleType: (type: ChangeType) => void;
  toggleStatus: (status: DecisionStatus) => void;
  toggleSemanticTag: (tag: SemanticTag) => void;
  setSearchTerm: (term: string) => void;
  resetFilters: () => void;
  isFiltering: boolean;
  filteredChanges: ChangeResponse[];
  activeFilterCount: number;
}

interface UseChangeFilterOptions {
  changes: ChangeResponse[];
  getStatus: (changeId: string) => DecisionStatus;
  /**
   * Monotonic counter from `useDecisions` — bumped on every status/meta
   * mutation. The second-stage memo depends on this so that A-decisions
   * (which flip status) re-evaluate the status filter only when
   * `statusFilter` has been narrowed below ALL_STATUSES. When the filter
   * is the full set, the second memo short-circuits and a statusVersion
   * bump does NOT trigger recomputation (the common case — every decision
   * stays visible).
   */
  statusVersion: number;
}

export function useChangeFilter({
  changes,
  getStatus,
  statusVersion,
}: UseChangeFilterOptions): UseChangeFilterResult {
  const [typeFilter, setTypeFilter] = useState<Set<ChangeType>>(
    () => new Set(DEFAULT_TYPES),
  );
  const [statusFilter, setStatusFilter] = useState<Set<DecisionStatus>>(
    () => new Set(ALL_STATUSES),
  );
  // P2-5: empty set means "no semantic constraint" (i.e. show all changes
  // regardless of whether they carry an amount/date/term tag). Each toggle
  // adds a required tag — a change must carry every selected tag to pass.
  const [semanticTagFilter, setSemanticTagFilter] = useState<Set<SemanticTag>>(
    () => new Set(),
  );
  const [searchTerm, setSearchTerm] = useState<string>('');

  const toggleType = useCallback((type: ChangeType): void => {
    setTypeFilter((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      // Never allow an empty set — reset to all if the last item was deselected.
      return next.size === 0 ? new Set(ALL_TYPES) : next;
    });
  }, []);

  const toggleStatus = useCallback((status: DecisionStatus): void => {
    setStatusFilter((prev) => {
      const next = new Set(prev);
      if (next.has(status)) next.delete(status);
      else next.add(status);
      return next.size === 0 ? new Set(ALL_STATUSES) : next;
    });
  }, []);

  const toggleSemanticTag = useCallback((tag: SemanticTag): void => {
    setSemanticTagFilter((prev) => {
      const next = new Set(prev);
      if (next.has(tag)) next.delete(tag);
      else next.add(tag);
      return next;
    });
  }, []);

  const resetFilters = useCallback((): void => {
    setTypeFilter(new Set(DEFAULT_TYPES));
    setStatusFilter(new Set(ALL_STATUSES));
    setSemanticTagFilter(new Set());
    setSearchTerm('');
  }, []);

  const isFiltering =
    typeFilter.size < ALL_TYPES.size ||
    statusFilter.size < ALL_STATUSES.size ||
    semanticTagFilter.size > 0 ||
    searchTerm.length > 0;

  const activeFilterCount =
    (ALL_TYPES.size - typeFilter.size) +
    (ALL_STATUSES.size - statusFilter.size) +
    semanticTagFilter.size;

  // Stage 1: type + search + semantic tags. Does NOT depend on statusFilter
  // / statusVersion so an A-decision (which only flips status) never
  // recomputes this stage.
  const filteredByTypeAndSearch = useMemo(() => {
    const lowerTerm = searchTerm.toLowerCase();
    return changes
      .filter((c) => typeFilter.has(c.changeType))
      .filter((c) => {
        if (semanticTagFilter.size === 0) return true;
        const tags = c.semanticTags ?? [];
        // Require every selected tag to be present (AND semantics).
        for (const required of semanticTagFilter) {
          if (!tags.includes(required)) return false;
        }
        return true;
      })
      .filter((c) => {
        if (!lowerTerm) return true;
        return (
          (c.contentA?.toLowerCase().includes(lowerTerm) ?? false) ||
          (c.contentB?.toLowerCase().includes(lowerTerm) ?? false)
        );
      });
  }, [changes, typeFilter, semanticTagFilter, searchTerm]);

  // Stage 2: status filter. Depends on statusVersion (a decision that flips
  // status needs this stage to re-evaluate). When statusFilter equals the
  // full set — the common case — short-circuit so a decision does NOT
  // trigger recomputation.
  const filteredChanges = useMemo(() => {
    if (statusFilter.size === ALL_STATUSES.size) return filteredByTypeAndSearch;
    return filteredByTypeAndSearch.filter((c) =>
      statusFilter.has(getStatus(c.id)),
    );
  }, [filteredByTypeAndSearch, statusFilter, getStatus, statusVersion]);

  return useMemo(
    () => ({
      typeFilter,
      statusFilter,
      semanticTagFilter,
      searchTerm,
      toggleType,
      toggleStatus,
      toggleSemanticTag,
      setSearchTerm,
      resetFilters,
      isFiltering,
      filteredChanges,
      activeFilterCount,
    }),
    [
      typeFilter,
      statusFilter,
      semanticTagFilter,
      searchTerm,
      toggleType,
      toggleStatus,
      toggleSemanticTag,
      setSearchTerm,
      resetFilters,
      isFiltering,
      filteredChanges,
      activeFilterCount,
    ],
  );
}

export { ALL_SEMANTIC_TAGS };
